import { Box, Stack } from '@mui/material';
import { FC, useLayoutEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import Text from '@/components/text';
import MarkdownContent from '@/components/markdown-content';
import FlatButton from '../flat-button';
import classNames from 'classnames';

const LINE_HEIGHT_PX = 24;
const FADE_TAIL_PX = 56;

interface Props {
  text: string;
  markdown?: boolean;
  maxLines?: number;
}

const useStyles = createUseStyles<string, { maxLines: number }>({
  readMore: {
    minWidth: 0,
    width: '100%',
  },
  clamped: {
    maxHeight: ({ maxLines }) => maxLines * LINE_HEIGHT_PX,
    overflow: 'hidden',
  },
  contentFade: {
    WebkitMaskImage: `linear-gradient(to bottom, #000 calc(100% - ${FADE_TAIL_PX}px), transparent 100%)`,
    maskImage: `linear-gradient(to bottom, #000 calc(100% - ${FADE_TAIL_PX}px), transparent 100%)`,
  },
});

const ReadMore: FC<Props> = ({ text, markdown = false, maxLines = 15 }) => {
  const styles = useStyles({ maxLines });
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element || isExpanded) {
      return;
    }
    setHasOverflow(element.scrollHeight > element.clientHeight);
  }, [text, isExpanded, markdown]);

  const contentClass = classNames(
    { [styles.clamped]: !isExpanded },
    { [styles.contentFade]: hasOverflow && !isExpanded }
  );

  const handleToggle = () => {
    setIsExpanded((previous) => !previous);
  };

  return (
    <Stack className={styles.readMore}>
      <Box ref={contentRef} className={contentClass}>
        {markdown ? <MarkdownContent message={text} /> : <Text size="small">{text}</Text>}
      </Box>
      {hasOverflow && (
        <Box mt={2}>
          <FlatButton
            size="small"
            color="link"
            onClick={handleToggle}
            label={isExpanded ? 'Show less' : 'Show more'}
          />
        </Box>
      )}
    </Stack>
  );
};

export default ReadMore;
